import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import {
  ArrowLeft,
  Trash2,
  Plus,
  Download,
  Edit,
  Eye,
  RefreshCw,
  FileText,
  Save,
  Briefcase,
  Copy
} from "lucide-react";
import { fetchProformas, createProforma, updateProforma, deleteProforma, API_URL } from "./api/proformaApi";
import GoLogo from "../../Golog.png";

// Get auth token from localStorage
const getAuthHeader = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = user.token || localStorage.getItem("authToken") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* ================= NUMBER TO WORD HELPER ================= */
const numberToWords = (num) => {
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
    if (n < 100000) return inWords(Math.floor(n / 1000)) + " Thousand " + inWords(n % 1000);
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + " Lakh " + inWords(n % 100000);
    return inWords(Math.floor(n / 10000000)) + " Crore " + inWords(n % 10000000);
  };
  const val = Math.floor(num);
  return val > 0 ? inWords(val) + " Rupees Only" : "Zero Rupees";
};

/* ================= IMAGE LOADING UTILITY FOR PDF ================= */
const getBase64Image = (imgUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = imgUrl;
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } catch (err) {
        console.error("Canvas conversion error:", err);
        resolve(null);
      }
    };
    img.onerror = (e) => {
      console.error("Failed to load image at URL:", imgUrl, e);
      resolve(null);
    };
  });
};

/* ================= CLIENT-SIDE PDF GENERATION ================= */
const generateProformaPDF = async (data, totals) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // ── TOP ROW: Logo | PROFORMA INVOICE (center) | Meta (right) ──────────────────
  let logoH = 0;

  if (data.logo) {
    try {
      let logoBase64 = data.logo;
      if (data.logo.startsWith("http") || data.logo.startsWith("./") || data.logo.startsWith("/") || data.logo.startsWith("data:image")) {
        if (!data.logo.startsWith("data:image")) {
          logoBase64 = await getBase64Image(data.logo);
        }
      }
      if (logoBase64) {
        let format = "PNG";
        if (logoBase64.includes("image/jpeg") || logoBase64.includes("image/jpg")) format = "JPEG";
        doc.addImage(logoBase64, format, 14, 7, 19, 12);
        logoH = 12;
      }
    } catch (e) {
      console.error("Logo PDF error:", e);
    }
  }

  // PROFORMA INVOICE – centered at top (original design)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 41, 59); // Dark slate
  doc.text("PROFORMA INVOICE", pageWidth / 2, 15, { align: "center" });

  // ── COMPANY HEADER (centered below top row) ───────────────────────────────
  let currentY = Math.max(logoH, 22) + 2;
  currentY += 2;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(30, 41, 59);
  doc.text((data.company || "GO2WEB SOLUTION").toUpperCase().trim(), pageWidth / 2, currentY, { align: "center" });
  currentY += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.2);
  doc.setTextColor(100, 116, 139); // Slate grey


  if (data.address) {
    const splitAddr = doc.splitTextToSize(String(data.address).trim(), 120);
    doc.text(splitAddr, pageWidth / 2, currentY, { align: "center" });
    currentY += splitAddr.length * 4;
  }
  if (data.phone) {
    doc.text(`Phone: ${String(data.phone).trim()} | Email: ${String(data.sellerEmail || "").trim()}`, pageWidth / 2, currentY, { align: "center" });
    currentY += 4.2;
  }

  // Reset text color to default
  doc.setTextColor(0, 0, 0);

  currentY += 1;
  doc.setLineWidth(0.5);
  doc.setDrawColor(180, 190, 210);
  doc.line(14, currentY, pageWidth - 14, currentY);
  currentY += 5;

  const buyerDetails = [
    data.proformaName || data.billingName,
    data.billingPhone && `Phone: ${data.billingPhone}`,
    data.email && `Email: ${data.email}`,
    (data.billingGstin || data.clientGstin) && `GSTIN: ${data.billingGstin || data.clientGstin}`,
    data.billingAddress
  ].filter(Boolean).join("\n");

  const formattedDate = data.date ? new Date(data.date).toLocaleDateString("en-IN") : "";
  const proformaDetails = [
    `Proforma No: ${data.proformaNo || ""}`,
    `Date: ${formattedDate}`,
    `Mode: ${data.paymentMode || "Online"}`
  ].join("\n");

  // Side-by-side details (Client left, Meta details right)
  autoTable(doc, {

    startY: currentY + 4,
    theme: "grid",
    head: [[
      { content: "Billed To Details (Recipient)", styles: { fontStyle: "bold", fontSize: 8.5 } },
      { content: "Proforma Details", styles: { fontStyle: "bold", fontSize: 8.5 } }
    ]],
    body: [[buyerDetails, proformaDetails]],
    headStyles: { fillColor: [248, 250, 252], textColor: [30, 41, 59], lineColor: [226, 232, 240] },
    bodyStyles: { fillColor: [248, 250, 252], textColor: [51, 65, 85], lineColor: [226, 232, 240] },
    styles: { fontSize: 8.5, cellPadding: 5 },
    columnStyles: {
      0: { cellWidth: 110 },
      1: { cellWidth: 72 }
    }
  });

  const tableData = data.items.map((item, i) => {
    const qty = Number(item.qty) || 0;
    const price = Number(item.price) || 0;
    const gross = qty * price;

    return [
      i + 1,
      item.name || "",
      item.hsn || "",
      qty % 1 === 0 ? qty : qty.toFixed(2),
      `Rs.${price.toFixed(2)}`,
      `Rs.${gross.toFixed(2)}`,
    ];
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 7,
    theme: "plain",
    head: [[
      { content: "#", styles: { halign: "center", fontStyle: "bold", fontSize: 8.5, textColor: [30, 41, 59], cellPadding: 4 } },
      { content: "Description / Item Name", styles: { halign: "left", fontStyle: "bold", fontSize: 8.5, textColor: [30, 41, 59], cellPadding: 4 } },
      { content: "HSN", styles: { halign: "center", fontStyle: "bold", fontSize: 8.5, textColor: [30, 41, 59], cellPadding: 4 } },
      { content: "Qty", styles: { halign: "center", fontStyle: "bold", fontSize: 8.5, textColor: [30, 41, 59], cellPadding: 4 } },
      { content: "Unit Price", styles: { halign: "right", fontStyle: "bold", fontSize: 8.5, textColor: [30, 41, 59], cellPadding: 4 } },
      { content: "Amount", styles: { halign: "right", fontStyle: "bold", fontSize: 8.5, textColor: [30, 41, 59], cellPadding: 4 } }
    ]],
    body: tableData,
    headStyles: {
      lineColor: [203, 213, 225],
      lineWidth: { bottom: 1 },
      fillColor: [255, 255, 255]
    },
    bodyStyles: {
      textColor: [51, 65, 85],
      fontSize: 8.5,
      cellPadding: 4,
      lineColor: [241, 245, 249],
      lineWidth: { bottom: 1 }
    },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 65, halign: "left" },
      2: { cellWidth: 20, halign: "center" },
      3: { cellWidth: 15, halign: "center" },
      4: { cellWidth: 35, halign: "right" },
      5: { cellWidth: 35, halign: "right", fontStyle: "bold" }
    }
  });
  // Totals
  const finalY = doc.lastAutoTable.finalY + 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const rightX = pageWidth - 20;

  const pdfSubtotal = data.items.reduce((acc, item) => {
    const qty = Number(item.qty) || 0;
    const price = Number(item.price) || 0;
    return acc + qty * price;
  }, 0);

  // Round off Grand Total
  const pdfGrandTotal = Math.round(pdfSubtotal);

  let yOffset = finalY;

  doc.text("Subtotal:", rightX - 60, yOffset);
  doc.text(`Rs.${pdfSubtotal.toFixed(2)}`, rightX, yOffset, { align: "right" });

  doc.setLineWidth(0.6);
  doc.setDrawColor(180, 190, 210);
  doc.line(rightX - 65, yOffset + 5, rightX, yOffset + 5);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);

  doc.text("GRAND TOTAL:", rightX - 60, yOffset + 11.5);
  doc.text(`Rs.${pdfGrandTotal.toFixed(2)}`, rightX, yOffset + 11.5, {
    align: "right"
  });

  doc.setFont("helvetica", "italic");
  doc.setFontSize(9.5);
  doc.text(
    `Amount in words: ${numberToWords(pdfGrandTotal)}`,
    15,
    yOffset + 20
  );

  currentY = yOffset + 32;

  // Bank details
  const selectedBank = data.selectedBank || (data.bankDetails && data.bankDetails.length > 0 ? data.bankDetails[0] : null);
  if (selectedBank) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.text("Bank Payment Details:", 15, currentY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    currentY += 6;
    if (selectedBank.holderName) { doc.text(`Account Holder: ${selectedBank.holderName}`, 15, currentY); currentY += 5; }
    doc.text(`Bank Name: ${selectedBank.bankName}`, 15, currentY); currentY += 5;
    doc.text(`Account No: ${selectedBank.accountNumber}`, 15, currentY); currentY += 5;
    doc.text(`IFSC Code: ${selectedBank.ifsc}`, 15, currentY); currentY += 5;
    if (selectedBank.address) { doc.text(`Branch: ${selectedBank.address}`, 15, currentY); currentY += 5; }
  }

  // Footer declaration box
  let footerY = currentY + 12;
  if (footerY + 35 > doc.internal.pageSize.getHeight()) {
    doc.addPage();
    footerY = 15;
  }

  doc.setLineWidth(0.6);
  doc.setDrawColor(30, 41, 59);
  doc.rect(15, footerY, (pageWidth - 30) / 2, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setFont("helvetica", "italic");
  doc.text("Thank You For Your Business!", 15 + ((pageWidth - 30) / 4), footerY + 15, { align: "center" });

  doc.rect(15 + ((pageWidth - 30) / 2), footerY, (pageWidth - 30) / 2, 22);
  doc.setFont("helvetica", "bold");
  doc.text(data.company || "Go2Web Solution", 15 + ((pageWidth - 30) * 0.75), footerY + 6, { align: "center" });
  doc.text("Authorised Signatory", pageWidth - 18, footerY + 16, { align: "right" });

  // Dark banner at the bottom (matching preview style)
  doc.setFillColor(30, 41, 59);
  doc.rect(15, footerY + 22, pageWidth - 30, 11, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.8);
  doc.setTextColor(255, 255, 255);
  doc.text("SUBJECT TO NCT OF AGRA JURISDICTION", pageWidth / 2, footerY + 26, { align: "center" });

  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.2);
  doc.setTextColor(215, 220, 230);
  doc.text("This is a Computer Generated Proforma", pageWidth / 2, footerY + 30.2, { align: "center" });

  // Reset colors back to default
  doc.setTextColor(0, 0, 0);

  doc.save(`proforma-${data.proformaNo || "INV-1001"}.pdf`);
};

/* ================= MAIN BILLING COMPONENT ================= */
export default function Billing() {
  const navigate = useNavigate();

  const [proformas, setProformas] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [salesData, setSalesData] = useState({ totalSales: 0, monthlySales: 0, weeklySales: 0, yearlySales: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // SELLER DEFAULTS — fixed, not editable in form
  const SELLER = {
    company: "Go2Web Solution",
    address: "Sanjay Palace, Agra, Uttar Pradesh 282002",
    phone: "+91  97179 85879",
    sellerEmail: "info@go2websolution.com",
    gstin: "09CKDPD6056L1ZX",
    pan: "",
    logo: GoLogo,
  };

  const [form, setForm] = useState({
    _id: null,
    proformaNo: "",
    date: new Date().toISOString().split("T")[0],
    paymentMode: "Online",

    // Recipient
    proformaName: "",
    email: "",
    billingAddress: "",
    billingPhone: "",
    billingGstin: "",

    // Items list
    items: [{ name: "", hsn: "9973", qty: 1, price: 1199 }],

    // Selected Bank
    selectedBank: null
  });

  const [selectedDomainId, setSelectedDomainId] = useState("");

  const [savedBanks, setSavedBanks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("savedBanks") || "[]");
    } catch {
      return [];
    }
  });

  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [bankForm, setBankForm] = useState({
    holderName: "",
    bankName: "",
    accountNumber: "",
    ifsc: "",
    address: ""
  });

  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);

  // Live calculations
  const subtotal = form.items.reduce((acc, item) => {
    const gross = (Number(item.qty) || 0) * (Number(item.price) || 0);
    return acc + gross;
  }, 0);

  const grandTotal = Math.round(subtotal);
  const totals = {
    subtotal: subtotal.toFixed(2),
    total: grandTotal.toFixed(2)
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const invRes = await fetchProformas(searchQuery, dateFilter, startDate, endDate);
      if (invRes.success) {
        setProformas(invRes.data);
        if (!form._id && !form.proformaNo) {
          const numbers = invRes.data.map(inv => {
            const match = inv.proformaNo?.match(/\d+/);
            return match ? parseInt(match[0]) : 0;
          }).filter(n => n > 0);
          const nextNo = numbers.length > 0 ? Math.max(...numbers) + 1 : 1001;
          setForm(prev => ({ ...prev, proformaNo: `INV-${nextNo}` }));
        }
      }

      // Fetch sales summary
      try {
        const salesRes = await fetch(`${API_URL}/api/sales-summary?type=proforma`, {
          headers: { ...getAuthHeader() }
        });
        const salesJson = await salesRes.json();
        if (salesJson.success) setSalesData(salesJson.data);
      } catch (e) {
        console.error("Sales summary fetch error:", e);
      }

      const domRes = await fetch(`${API_URL}/api/domains`, {
        headers: { ...getAuthHeader() }
      });
      const domData = await domRes.json();
      if (domData.success) {
        setDomains(domData.domains || []);
      }
    } catch (err) {
      console.error("Error loading proforma data:", err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, dateFilter, startDate, endDate, form._id, form.proformaNo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    localStorage.setItem("savedBanks", JSON.stringify(savedBanks));
  }, [savedBanks]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...form.items];
    updated[index][field] = value;
    setForm(prev => ({ ...prev, items: updated }));
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { name: "", hsn: "9973", qty: 1, price: 1199 }]
    }));
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) return;
    const updated = form.items.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, items: updated }));
  };

  // const handleLogoChange = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     setLogoFile(file);
  //     setLogoPreview(URL.createObjectURL(file));
  //   }
  // };

  const handleDomainSelectChange = (e) => {
    const id = e.target.value;
    setSelectedDomainId(id);
    if (!id) return;

    const domain = domains.find(d => d.id === id);
    if (domain) {
      setForm(prev => {
        const items = [...prev.items];
        if (items.length === 1 && items[0].name === "") {
          items[0] = {
            name: `Domain Renewal - ${domain.domainName} (${domain.registrar || "General"})`,
            hsn: "9973",
            qty: 1,
            price: Number(domain.cost) || 1199
          };
        } else {
          items.push({
            name: `Domain Renewal - ${domain.domainName} (${domain.registrar || "General"})`,
            hsn: "9973",
            qty: 1,
            price: Number(domain.cost) || 1199
          });
        }

        return {
          ...prev,
          proformaName: domain.clientName || prev.proformaName,
          billingPhone: domain.whatsappNo || prev.billingPhone,
          billingAddress: domain.notes ? `Domain: ${domain.domainName}\n${domain.notes}` : `Domain: ${domain.domainName}\nHosting: ${domain.hostingProvider || "N/A"}`,
          items
        };
      });
    }
  };

  const handleSaveProforma = async (e) => {
    e.preventDefault();
    if (!form.proformaName || !form.proformaNo || !form.items[0].name) {
      Swal.fire("Incomplete Fields", "Please fill in the Recipient Name, Proforma Number, and at least one Item Name.", "warning");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      // Merge seller defaults into submission
      const submission = { ...SELLER, ...form };

      Object.entries(submission).forEach(([key, value]) => {
        if (key === "_id" && !value) return;
        if (key === "selectedBank" && value) {
          formData.append("bankDetails", JSON.stringify([value]));
          formData.append("selectedBank", JSON.stringify(value));
        } else if (key === "items") {
          formData.append(key, JSON.stringify(value));
        } else if (key !== "logo" && key !== "selectedBank") {
          formData.append(key, value);
        }
      });

      if (logoFile) {
        formData.append("logo", logoFile);
      } else if (SELLER.logo) {
        formData.append("logo", SELLER.logo);
      }

      let res;
      if (form._id) {
        res = await updateProforma(form._id, formData);
      } else {
        res = await createProforma(formData);
      }

      if (res.success) {
        Swal.fire("Saved!", "Proforma saved successfully.", "success");
        const updatedProforma = res.data;
        const subT = Number(updatedProforma.subtotal || 0);
        const currentTotals = {
          subtotal: subT.toFixed(2),
          total: Number(updatedProforma.total || 0).toFixed(2)
        };
        generateProformaPDF({ ...updatedProforma, ...SELLER }, currentTotals);
        resetProformaForm();
        loadData();
      } else {
        Swal.fire("Error", res.message || "Failed to save proforma.", "error");
      }
    } catch (err) {
      console.error("Save error:", err);
      Swal.fire("Save Error", "Could not connect to server or save proforma.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditProforma = (proforma) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setForm({
      _id: proforma._id,
      proformaNo: proforma.proformaNo || "",
      date: proforma.date ? new Date(proforma.date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
      paymentMode: proforma.paymentMode || "Online",

      proformaName: proforma.proformaName || "",
      email: proforma.email || "",
      billingAddress: proforma.billingAddress || "",
      billingPhone: proforma.billingPhone || "",
      billingGstin: proforma.billingGstin || proforma.clientGstin || "",

      items: proforma.items && proforma.items.length > 0 ? proforma.items : [{ name: "", hsn: "9973", qty: 1, price: 1199 }],
      selectedBank: proforma.selectedBank || (proforma.bankDetails && proforma.bankDetails.length > 0 ? proforma.bankDetails[0] : null)
    });

    if (proforma.logo) {
      setLogoPreview(proforma.logo);
    } else {
      setLogoPreview("");
    }
    setLogoFile(null);
  };

  const handleDownloadProforma = (proforma) => {
    const itemSub = proforma.items.reduce(
      (acc, item) =>
        acc + (Number(item.qty) || 0) * (Number(item.price) || 0),
      0
    );

    const tot = Math.round(itemSub);

    const invTotals = {
      subtotal: itemSub.toFixed(2),
      total: tot.toFixed(2)
    };
    generateProformaPDF({ ...proforma, ...SELLER }, invTotals);
  };

  const handleDeleteProforma = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This proforma will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {
        const res = await deleteProforma(id);
        if (res.success) {
          Swal.fire("Deleted!", "Proforma deleted successfully.", "success");
          loadData();
        } else {
          Swal.fire("Error", res.message || "Failed to delete proforma.", "error");
        }
      } catch (err) {
        console.error("Delete proforma error:", err);
        Swal.fire("Delete Error", "Could not complete delete operation.", "error");
      }
    }
  };

  // ── CLONE INVOICE ──────────────────────────────────────────────────────────
  const handleCloneProforma = async (proforma) => {
    // Suggest next proforma number
    const numbers = proformas.map(inv => {
      const match = inv.proformaNo?.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    }).filter(n => n > 0);
    const nextNo = numbers.length > 0 ? Math.max(...numbers) + 1 : 1001;
    const suggestedNo = `INV-${nextNo}`;

    const { value: newProformaNo, isConfirmed } = await Swal.fire({
      title: '📋 Clone Proforma',
      html: `
        <p style="margin-bottom:12px;color:#475569;font-size:0.9rem">
          Cloning: <strong>${proforma.proformaNo}</strong> → New Proforma<br/>
          All details same rahenge, sirf Proforma No change hoga.
        </p>
        <input id="swal-inv-no" class="swal2-input" value="${suggestedNo}" placeholder="New Proforma No" style="font-size:1rem;font-weight:700;color:#4f46e5" />
      `,
      showCancelButton: true,
      confirmButtonText: 'Clone & Edit',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#7c3aed',
      focusConfirm: false,
      preConfirm: () => {
        const val = document.getElementById('swal-inv-no').value.trim();
        if (!val) { Swal.showValidationMessage('Proforma No cannot be empty!'); return false; }
        return val;
      }
    });

    if (!isConfirmed || !newProformaNo) return;

    // Check for duplicate
    const exists = proformas.find(inv => inv.proformaNo === newProformaNo);
    if (exists) {
      Swal.fire('Duplicate!', `Proforma No "${newProformaNo}" already exists. Please choose a different number.`, 'warning');
      return;
    }

    // Populate form with cloned data + new proforma number
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setForm({
      _id: null,           // new proforma — no _id
      proformaNo: newProformaNo,
      date: new Date().toISOString().split('T')[0],  // today's date
      paymentMode: proforma.paymentMode || 'Online',
      proformaName: proforma.proformaName || '',
      email: proforma.email || '',
      billingAddress: proforma.billingAddress || '',
      billingPhone: proforma.billingPhone || '',
      billingGstin: proforma.billingGstin || proforma.clientGstin || '',
      items: proforma.items && proforma.items.length > 0
        ? proforma.items.map(item => ({ ...item }))
        : [{ name: '', hsn: '9973', qty: 1, price: 1199 }],
      selectedBank: proforma.selectedBank || (proforma.bankDetails && proforma.bankDetails.length > 0 ? proforma.bankDetails[0] : null)
    });
    setLogoPreview('');
    setLogoFile(null);
    setSelectedDomainId('');

    Swal.fire({
      icon: 'success',
      title: 'Cloned!',
      text: `Proforma cloned as ${newProformaNo}. Review details and click Save & Download.`,
      timer: 2500,
      showConfirmButton: false
    });
  };

  const resetProformaForm = () => {
    setForm({
      _id: null,
      proformaNo: "",
      date: new Date().toISOString().split("T")[0],
      paymentMode: "Online",
      proformaName: "",
      email: "",
      billingAddress: "",
      billingPhone: "",
      billingGstin: "",
      items: [{ name: "", hsn: "9973", qty: 1, price: 1199 }],
      selectedBank: savedBanks.length > 0 ? savedBanks[0] : null
    });
    setLogoPreview("");
    setLogoFile(null);
    setSelectedDomainId("");
  };

  const handleAddBank = (e) => {
    e.preventDefault();
    if (!bankForm.bankName || !bankForm.accountNumber) {
      Swal.fire("Warning", "Bank Name & Account Number are required.", "warning");
      return;
    }
    const updated = [...savedBanks, bankForm];
    setSavedBanks(updated);
    setForm(prev => ({ ...prev, selectedBank: bankForm }));
    setBankModalOpen(false);
    setBankForm({ holderName: "", bankName: "", accountNumber: "", ifsc: "", address: "" });
    Swal.fire("Success", "Bank account details saved.", "success");
  };

  const handleBankSelect = (bank) => {
    setForm(prev => ({ ...prev, selectedBank: bank }));
  };

  const handleDeleteBank = (index, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this bank account?")) {
      const bankToDelete = savedBanks[index];
      setSavedBanks(prev => prev.filter((_, i) => i !== index));
      if (form.selectedBank && form.selectedBank.accountNumber === bankToDelete.accountNumber) {
        setForm(prev => ({ ...prev, selectedBank: null }));
      }
    }
  };


  // ================= PAGINATION =================
  const totalPages = Math.ceil(proformas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProformas = proformas.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFilter]);

  return (
    <div style={styles.appWrapper} className="billing-wrapper">
      {/* HEADER BAR */}
      <div style={styles.header} className="billing-header">
        <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
          <button
            className="btn btn-secondary btn-icon"
            onClick={() => navigate("/")}
            title="Back to Dashboard"
            style={{ borderRadius: "12px", padding: "10px" }}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 style={styles.brandTitle}>🧾 Go2Web Solution — Proforma</h1>
            <p style={styles.brandSubtitle}>Professional Proforma Manager</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button
            className="btn btn-secondary"
            onClick={resetProformaForm}
            style={{ borderRadius: "12px", fontWeight: "700", fontSize: "0.85rem" }}
          >
            <RefreshCw size={15} style={{ marginRight: "6px" }} /> New Proforma
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/")}
            style={{ borderRadius: "12px", fontWeight: "700", fontSize: "0.85rem" }}
          >
            ← Dashboard
          </button>
        </div>
      </div>

      {/* WORKSPACE CONTENT CONTAINER */}
      <div style={styles.workspaceGrid} className="billing-grid">

        {/* LEFT COLUMN: CLIENT + ITEMS */}
        <div className="glass-panel" style={styles.formContainer}>
          <h2 style={styles.sectionHeader}>
            <span style={styles.sectionIconWrap}><FileText size={18} color="#fff" /></span>
            Proforma Information
          </h2>
          <form onSubmit={handleSaveProforma} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

            {/* ── INVOICE META SECTION ── */}
            <div style={{ ...styles.formSection, borderLeft: "4px solid #6366f1" }}>
              <h3 style={{ ...styles.subSectionHeader, color: "#4f46e5", display: "flex", alignItems: "center", gap: "6px" }}>
                📋 Proforma Details
              </h3>
              <p style={{ color: "#4f46e5", fontSize: "0.85rem", size: "45px" }}></p>
              <div style={styles.fieldsGrid3} className="billing-fields-3">
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Proforma No *</label>
                  <input type="text" name="proformaNo" value={form.proformaNo} onChange={handleFormChange} className="form-control" required />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Proforma Date *</label>
                  <input type="date" name="date" value={form.date} onChange={handleFormChange} className="form-control" required />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Payment Mode</label>
                  <select name="paymentMode" value={form.paymentMode} onChange={handleFormChange} className="form-control">
                    <option value="Online">Online</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Card">Card</option>
                  </select>
                </div>
              </div>
            </div>

            {/* ── CLIENT / RECIPIENT SECTION ── */}
            <div style={{ ...styles.formSection, borderLeft: "4px solid #10b981" }}>
              <h3 style={{ ...styles.subSectionHeader, color: "#10b981" }}>👤 Client / Billed To</h3>

              {/* Domain Auto-fill */}
             

              <div style={styles.fieldsGrid3} className="billing-fields-3">
                <div className="form-group">
                  <label className="form-label">Client Name *</label>
                  <input type="text" name="proformaName" value={form.proformaName} onChange={handleFormChange} className="form-control" placeholder="Client / Org Name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Client Email</label>
                  <input type="email" name="email" value={form.email} onChange={handleFormChange} className="form-control" placeholder="client@email.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Client Phone</label>
                  <input type="text" name="billingPhone" value={form.billingPhone} onChange={handleFormChange} className="form-control" placeholder="WhatsApp / Phone" />
                </div>
              </div>
              <div style={styles.fieldsGrid2} className="billing-fields-2">
                <div className="form-group">
                  <label className="form-label">Client Address</label>
                  <input type="text" name="billingAddress" value={form.billingAddress} onChange={handleFormChange} className="form-control" placeholder="Billing Address" />
                </div>
              </div>
            </div>

            {/* ── BANK DETAILS ── */}
            <div style={{ ...styles.formSection, borderLeft: "4px solid #06b6d4" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: "700", color: "#1e293b" }}>🏦 Bank Details</h3>
                <button type="button" className="btn btn-secondary" style={{ padding: "6px 12px", fontSize: "0.75rem" }} onClick={() => setBankModalOpen(true)}>
                  <Plus size={12} /> Add Bank
                </button>
              </div>

              {savedBanks.length > 0 ? (
                <div style={styles.bankCardsGrid} className="billing-bank-grid">
                  {savedBanks.map((bank, index) => {
                    const isSelected = form.selectedBank && form.selectedBank.accountNumber === bank.accountNumber;
                    return (
                      <div
                        key={index}
                        onClick={() => handleBankSelect(bank)}
                        style={{
                          ...styles.bankCard,
                          borderColor: isSelected ? "#4f46e5" : "#e2e8f0",
                          backgroundColor: isSelected ? "#f5f3ff" : "#fff"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontWeight: "700", fontSize: "0.85rem", color: isSelected ? "#4f46e5" : "#1e293b" }}>
                              {bank.bankName}
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "#64748b" }}>A/C: {bank.accountNumber}</div>
                            <div style={{ fontSize: "0.75rem", color: "#64748b" }}>IFSC: {bank.ifsc}</div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => handleDeleteBank(index, e)}
                            style={{
                              background: "none", border: "none", color: "#ef4444", cursor: "pointer", padding: "2px",
                              display: "flex", alignItems: "center", justifyContent: "center"
                            }}
                            title="Delete Bank"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <div
                    onClick={() => setForm(prev => ({ ...prev, selectedBank: null }))}
                    style={{
                      ...styles.bankCard,
                      borderColor: !form.selectedBank ? "#4f46e5" : "#e2e8f0",
                      backgroundColor: !form.selectedBank ? "#f5f3ff" : "#fff",
                      justifyContent: "center",
                      alignItems: "center"
                    }}
                  >
                    <div style={{ fontSize: "0.8rem", fontWeight: "600", color: !form.selectedBank ? "#4f46e5" : "#64748b" }}>
                      No Bank Details
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: "0.8rem", color: "#64748b", margin: 0, fontStyle: "italic" }}>
                  No saved banks yet. Click "Add Bank" to store bank info.
                </p>
              )}
            </div>

            {/* ── LINE ITEMS ── */}
            <div style={{ ...styles.formSection, borderLeft: "4px solid #8b5cf6" }}>
              <h3 style={{ ...styles.subSectionHeader, color: "#6d28d9" }}>📦 Proforma Line Items</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {form.items.map((item, index) => (
                  <div key={index} style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.75rem", fontWeight: "700", color: "#6d28d9", minWidth: "22px", textAlign: "center" }}>#{index + 1}</span>

                    <div className="form-group" style={{ flex: 3, minWidth: "180px", margin: 0 }}>
                      <input
                        type="text"
                        placeholder="Item / Service Description *"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, "name", e.target.value)}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group" style={{ flex: 0.7, minWidth: "70px", margin: 0 }}>
                      <input type="text" placeholder="HSN" value={item.hsn || ""} onChange={(e) => handleItemChange(index, "hsn", e.target.value)} className="form-control" />
                    </div>
                    <div className="form-group" style={{ width: "80px", minWidth: "70px", margin: 0 }}>
                      <input
                        type="number"
                        placeholder="Qty"
                        value={item.qty}
                        onChange={(e) => { const v = e.target.value; handleItemChange(index, "qty", v === "" ? "" : Number(v)); }}
                        className="form-control"
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1, minWidth: "100px", margin: 0 }}>
                      <input
                        type="number"
                        placeholder="Rate (₹)"
                        value={item.price}
                        onChange={(e) => { const v = e.target.value; handleItemChange(index, "price", v === "" ? "" : Number(v)); }}
                        className="form-control"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      disabled={form.items.length <= 1}
                      className="btn btn-danger btn-icon"
                      style={{ padding: "10px", height: "42px", flexShrink: 0 }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addItem}
                style={{ marginTop: "8px", width: "100%", border: "1px dashed #a78bfa" }}
              >
                <Plus size={16} /> Add Item Line
              </button>
            </div>

            {/* Total display & actions */}
            <div style={styles.formFooter} className="billing-footer">
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "600" }}>SUBTOTAL</span>

                <span style={{ fontSize: "1.6rem", fontWeight: "800", color: "#10b981" }}>
                  ₹{grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button type="submit" className="btn btn-primary" style={{ padding: "14px 24px" }} disabled={loading}>
                  <Save size={18} />
                  <span>{form._id ? "Update & Download" : "Save & Download"}</span>
                </button>
              </div>
            </div>

          </form>
        </div>

        {/* RIGHT COLUMN: INVOICE META + LIVE PREVIEW */}
        <div style={styles.previewStickyContainer}>
          <div style={styles.previewFixedWrapper}>

            {/* ── LIVE PAPER INVOICE PREVIEW ── */}
            <div style={styles.previewHeading}>
              <Eye size={18} color="#10b981" />
              <span>Real-time Paper Proforma Preview</span>
            </div>

            <div style={styles.proformaPaper}>
              {/* Top Row: Logo & Proforma Title */}
              <div style={styles.previewTopRow}>
                <div>
                  {logoPreview || SELLER.logo ? (
                    <img src={logoPreview || SELLER.logo} alt="Company Logo" style={styles.previewLogo} />
                  ) : (
                    <div style={styles.logoPlaceholder}>LOGO</div>
                  )}
                </div>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={styles.taxProformaHeader}>PROFORMA INVOICE</div>
                </div>
                <div style={{ width: "110px" }}></div> {/* Balance offset to keep title centered */}
              </div>

              {/* Seller Name Header (Centered) */}
              <div style={{ textAlign: "center", marginBottom: "12px", borderBottom: "1px solid #e2e8f0", paddingBottom: "8px" }}>
                <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "#1e293b", marginBottom: "4px" }}>
                  {SELLER.company.toUpperCase()}
                </div>
                <div style={styles.subMeta}>{SELLER.address}</div>
                <div style={styles.subMeta}>Phone: {SELLER.phone} | Email: {SELLER.sellerEmail}</div>
              </div>

              {/* Recipient & Proforma details side-by-side */}
              <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                {/* Left Column: Billed To */}
                <div style={{ flex: 1.2, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px" }}>
                  <div style={styles.previewBoxHeader}>Billed To / Recipient</div>
                  <div style={styles.previewBoxBody}>
                    <b>{form.proformaName || "Client Billing Name"}</b><br />
                    {form.billingPhone && <>Phone: {form.billingPhone}<br /></>}
                    {form.email && <>Email: {form.email}<br /></>}
                    {form.billingGstin && <>GSTIN: {form.billingGstin}<br /></>}
                    {form.billingAddress && <>{form.billingAddress}</>}
                  </div>
                </div>
                {/* Right Column: Proforma Details */}
                <div style={{ flex: 0.8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px" }}>
                  <div style={styles.previewBoxHeader}>Proforma Details</div>
                  <div style={styles.previewBoxBody}>
                    <b>Proforma No:</b> {form.proformaNo || "AUTO"}<br />
                    <b>Date:</b> {form.date ? new Date(form.date).toLocaleDateString("en-IN") : ""}<br />
                    <b>Mode:</b> {form.paymentMode || "Online"}
                  </div>
                </div>
              </div>

              {/* Table of items */}
              <table style={styles.previewTable}>
                <thead>
                  <tr>
                    <th style={styles.previewTh}>#</th>
                    <th style={{ ...styles.previewTh, textAlign: "left" }}>Description / Item</th>
                    <th style={styles.previewTh}>HSN</th>
                    <th style={styles.previewTh}>Qty</th>
                    <th style={{ ...styles.previewTh, textAlign: "right" }}>Unit Price</th>
                    <th style={{ ...styles.previewTh, textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => {
                    const qty = Number(item.qty) || 0;
                    const price = Number(item.price) || 0;
                    const gross = qty * price;

                    return (
                      <tr key={idx}>
                        <td style={styles.previewTd}>{idx + 1}</td>
                        <td style={{ ...styles.previewTd, textAlign: "left", fontWeight: "600" }}>{item.name || "Unnamed Item"}</td>
                        <td style={styles.previewTd}>{item.hsn || "-"}</td>
                        <td style={styles.previewTd}>{qty % 1 === 0 ? qty : qty.toFixed(2)}</td>
                        <td style={{ ...styles.previewTd, textAlign: "right" }}>₹{price.toFixed(2)}</td>
                        <td style={{ ...styles.previewTd, textAlign: "right", fontWeight: "600" }}>₹{gross.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals */}
              <div style={styles.previewTotalsContainer}>
                <div style={{ flex: 1.2, paddingRight: "10px" }}>
                  <div style={{ fontSize: "0.72rem", fontStyle: "italic", marginBottom: "8px" }}>
                    <b>Amount In Words:</b><br />
                    {numberToWords(grandTotal)}
                  </div>
                  {form.selectedBank && (
                    <div style={styles.bankDetailPreviewBox}>
                      <b>Bank Details:</b><br />
                      Bank: {form.selectedBank.bankName}<br />
                      A/C No: {form.selectedBank.accountNumber}<br />
                      IFSC: {form.selectedBank.ifsc}
                    </div>
                  )}
                </div>
                <div style={{ flex: 0.8, display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={styles.totalRowText}>
                    <span>Subtotal:</span>
                    <span>₹{totals.subtotal}</span>
                  </div>
                  <div style={{ ...styles.totalRowText, borderTop: "1px solid #cbd5e1", paddingTop: "5px", marginTop: "4px", fontWeight: "800", fontSize: "0.9rem", color: "#4f46e5" }}>
                    <span>Grand Total:</span>
                    <span>₹{totals.total}</span>
                  </div>
                </div>
              </div>

              {/* Declaration footer */}
              <div style={styles.previewFooterBox}>
                <div style={{ flex: 1, borderRight: "1px solid #000", padding: "4px", textAlign: "left" }}>
                  Thank You For Your Business!<br />
                </div>
                <div style={{ flex: 1, padding: "4px" }}>
                  <b>For {SELLER.company}</b><br />
                  <br />
                  <span style={{ fontSize: "0.65rem", display: "block", textAlign: "right" }}>Authorized Signatory</span>
                </div>
              </div>

              {/* Jurisdiction Footer Banner */}
              <div style={{
                marginTop: "6px",
                border: "1px solid #1e293b",
                borderRadius: "0 0 6px 6px",
                background: "#1e293b",
                padding: "5px 10px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: "2px"
              }}>
                <span style={{ fontSize: "0.62rem", fontWeight: "700", color: "#ffffff", letterSpacing: "0.4px", textTransform: "uppercase" }}>
                  SUBJECT TO NCT OF AGRA JURISDICTION
                </span>
                <span style={{ fontSize: "0.6rem", color: "rgba(199,210,254,0.9)", fontStyle: "italic" }}>
                  This is a Computer Generated Proforma
                </span>
              </div>

            </div>
          </div>
        </div>

      </div>

      {/* ALL SAVED PROFORMAS LIST TABLE */}
      <div className="glass-panel" style={styles.listSection}>
        <div style={styles.listHeaderRow}>
          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "800", display: "flex", alignItems: "center", gap: "10px" }}>
            <Briefcase size={22} color="#4f46e5" />
            <span>Saved Proformas ({proformas.length})</span>
          </h2>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
            <input
              type="text"
              placeholder="Search Client or Proforma No..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control"
              style={{ width: "210px", background: "#f8fafc" }}
            />
            <select
              value={dateFilter}
              onChange={(e) => { setDateFilter(e.target.value); setStartDate(""); setEndDate(""); }}
              className="form-control"
              style={{ width: "140px", background: "#f8fafc", fontWeight: "600" }}
            >
              <option value="all">All Dates</option>
              <option value="day">Today</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="specific_date">Specific Date</option>
              <option value="custom">Custom Range</option>
            </select>
            {dateFilter === "specific_date" && (
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setEndDate(e.target.value); }}
                className="form-control"
                style={{ width: "145px", background: "#f0fdf4", borderColor: "#86efac", fontWeight: "600" }}
                title="Select a specific date"
              />
            )}
            {dateFilter === "custom" && (
              <>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-control"
                  style={{ width: "145px", background: "#fff7ed", borderColor: "#fdba74", fontWeight: "600" }}
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-control"
                  style={{ width: "145px", background: "#fff7ed", borderColor: "#fdba74", fontWeight: "600" }}
                />
              </>
            )}
          </div>
        </div>

        {/* ── SALES SUMMARY CARDS ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: "12px", margin: "16px 0" }}>
          {[
            { label: "Total Sales", value: salesData.totalSales, color: "#4f46e5", bg: "#eef2ff", border: "#c7d2fe", emoji: "💰" },
            { label: "This Month", value: salesData.monthlySales, color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", emoji: "📅" },
            { label: "This Week", value: salesData.weeklySales, color: "#d97706", bg: "#fffbeb", border: "#fde68a", emoji: "📆" },
            { label: "This Year", value: salesData.yearlySales, color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", emoji: "🗓️" },
          ].map((card) => (
            <div key={card.label} style={{
              background: card.bg,
              border: `1.5px solid ${card.border}`,
              borderRadius: "14px",
              padding: "14px 18px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "0.75rem", fontWeight: "700", color: card.color, display: "flex", alignItems: "center", gap: "6px" }}>
                <span>{card.emoji}</span> {card.label}
              </div>
              <div style={{ fontSize: "1.3rem", fontWeight: "900", color: card.color }}>
                ₹{Math.round(Number(card.value || 0)).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: "0.7rem", color: "#64748b", fontStyle: "italic" }}>Proforma</div>
            </div>
          ))}
        </div>

        {proformas.length > 0 ? (
          <div style={{ overflowX: "auto", marginTop: "16px" }}>
            <table style={styles.listTable}>
              <thead>
                <tr>
                  <th style={styles.listTh}>Proforma No</th>
                  <th style={styles.listTh}>Client</th>
                  <th style={styles.listTh}>Date</th>
                  <th style={{ ...styles.listTh, textAlign: "right" }}>Total Amount</th>
                  <th style={{ ...styles.listTh, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProformas.map((inv) => {
                  const cleanDate = inv.date ? new Date(inv.date).toLocaleDateString("en-IN") : new Date(inv.createdAt).toLocaleDateString("en-IN");
                  return (
                    <tr key={inv._id} className="table-row-hover" style={styles.listTr}>
                      <td style={{ ...styles.listTd, fontWeight: "700", color: "#4f46e5" }}>{inv.proformaNo}</td>
                      <td style={{ ...styles.listTd, fontWeight: "600" }}>{inv.proformaName}</td>
                      <td style={styles.listTd}>{cleanDate}</td>
                      <td style={{ ...styles.listTd, textAlign: "right", fontWeight: "800", color: "#10b981" }}>
                        ₹{Math.round(Number(inv.total || 0)).toLocaleString("en-IN")}
                      </td>
                      <td style={{ ...styles.listTd, textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                          <button onClick={() => handleEditProforma(inv)} className="btn btn-secondary btn-icon" style={{ padding: "6px" }} title="Edit Proforma">
                            <Edit size={14} color="#f59e0b" />
                          </button>
                          <button
                            onClick={() => handleCloneProforma(inv)}
                            className="btn btn-secondary btn-icon"
                            style={{ padding: "6px", borderColor: "#7c3aed", background: "#f5f3ff" }}
                            title="Clone Proforma (same data, new Proforma No)"
                          >
                            <Copy size={14} color="#7c3aed" />
                          </button>
                          <button onClick={() => handleDownloadProforma(inv)} className="btn btn-secondary btn-icon" style={{ padding: "6px" }} title="Download PDF">
                            <Download size={14} color="#10b981" />
                          </button>
                          <button onClick={() => handleDeleteProforma(inv._id)} className="btn btn-danger btn-icon" style={{ padding: "6px" }} title="Delete Proforma">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* PAGINATION */}
            {proformas.length > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "18px", paddingTop: "16px", borderTop: "1px solid #e2e8f0", gap: "12px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "0.82rem", color: "#64748b" }}>
                  <span>Showing <strong style={{ color: "#1e293b" }}>{startIndex + 1}</strong> to <strong style={{ color: "#1e293b" }}>{Math.min(endIndex, proformas.length)}</strong> of <strong style={{ color: "#1e293b" }}>{proformas.length}</strong> proformas</span>
                  <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="form-control" style={{ width: "80px", padding: "7px 8px", fontSize: "0.8rem" }}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span>per page</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <button type="button" className="btn btn-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)} style={{ padding: "7px 12px", fontSize: "0.8rem", opacity: currentPage === 1 ? 0.5 : 1 }}>← Prev</button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                    <button type="button" key={page} onClick={() => setCurrentPage(page)} className="btn" style={{ minWidth: "34px", height: "34px", padding: "5px 8px", borderRadius: "8px", border: "1px solid", borderColor: currentPage === page ? "#4f46e5" : "#e2e8f0", background: currentPage === page ? "#4f46e5" : "#fff", color: currentPage === page ? "#fff" : "#475569", fontWeight: "700", fontSize: "0.8rem" }}>
                      {page}
                    </button>
                  ))}
                  <button type="button" className="btn btn-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)} style={{ padding: "7px 12px", fontSize: "0.8rem", opacity: currentPage === totalPages ? 0.5 : 1 }}>Next →</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
            <FileText size={48} style={{ opacity: 0.4, marginBottom: "12px" }} />
            <div style={{ fontWeight: "600" }}>No saved proformas found.</div>
            <div style={{ fontSize: "0.85rem" }}>Try adjusting your search or generate a new proforma above.</div>
          </div>
        )}
      </div>

      {/* BANK MODAL */}
      {bankModalOpen && (
        <div className="modal-overlay" onClick={() => setBankModalOpen(false)}>
          <div className="modal-content" style={{ maxWidth: "480px" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "700" }}>Add Bank Account</h3>
              <button onClick={() => setBankModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#64748b" }}>&times;</button>
            </div>
            <form onSubmit={handleAddBank} style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div className="form-group">
                <label className="form-label">Account Holder Name</label>
                <input type="text" value={bankForm.holderName} onChange={(e) => setBankForm({ ...bankForm, holderName: e.target.value })} className="form-control" placeholder="e.g. John Doe" required />
              </div>
              <div className="form-group">
                <label className="form-label">Bank Name</label>
                <input type="text" value={bankForm.bankName} onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })} className="form-control" placeholder="e.g. ICICI Bank" required />
              </div>
              <div className="form-group">
                <label className="form-label">Account Number</label>
                <input type="text" value={bankForm.accountNumber} onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })} className="form-control" placeholder="e.g. 100293849102" required />
              </div>
              <div className="form-group">
                <label className="form-label">IFSC Code</label>
                <input type="text" value={bankForm.ifsc} onChange={(e) => setBankForm({ ...bankForm, ifsc: e.target.value.toUpperCase() })} className="form-control" placeholder="e.g. ICIC0000102" required />
              </div>
              <div className="form-group">
                <label className="form-label">Branch Address</label>
                <input type="text" value={bankForm.address} onChange={(e) => setBankForm({ ...bankForm, address: e.target.value })} className="form-control" placeholder="e.g. Noida Sector 62 Branch" />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
                <button type="button" className="btn btn-secondary" onClick={() => setBankModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Bank Info</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

/* ================= STYLES ================= */
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap";
document.head.appendChild(fontLink);

const styles = {
  appWrapper: {
    maxWidth: "1440px",
    margin: "0 auto",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    fontFamily: "'Inter', sans-serif"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 32px",
    background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4f46e5 100%)",
    borderRadius: "20px",
    boxShadow: "0 8px 32px rgba(79, 70, 229, 0.35)"
  },
  brandTitle: {
    fontSize: "1.45rem",
    fontWeight: "800",
    letterSpacing: "-0.3px",
    color: "#ffffff",
    margin: 0,
    textShadow: "0 1px 2px rgba(0,0,0,0.2)"
  },
  brandSubtitle: {
    fontSize: "0.78rem",
    color: "rgba(199, 210, 254, 0.9)",
    fontWeight: "500",
    margin: 0,
    marginTop: "3px",
    letterSpacing: "0.2px"
  },
  workspaceGrid: {
    display: "grid",
    gridTemplateColumns: "1.35fr 1fr",
    gap: "24px",
    alignItems: "start"
  },
  formContainer: {
    padding: "28px",
    background: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)"
  },
  sectionIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
    borderRadius: "10px",
    padding: "7px",
    marginRight: "10px"
  },
  sectionHeader: {
    fontSize: "1.1rem",
    fontWeight: "800",
    color: "#0f172a",
    margin: "0 0 22px 0",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    borderBottom: "2px solid #e8e8f0",
    paddingBottom: "14px",
    letterSpacing: "-0.2px"
  },
  formSection: {
    border: "1px solid #ede9fe",
    padding: "18px 20px",
    borderRadius: "14px",
    background: "linear-gradient(135deg, #fafafa 0%, #f8f7ff 100%)",
    display: "flex",
    flexDirection: "column",
    gap: "14px"
  },
  subSectionHeader: {
    margin: "0 0 4px 0",
    fontSize: "0.92rem",
    fontWeight: "700",
    color: "#1e293b",
    letterSpacing: "-0.1px"
  },
  fieldsGrid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px"
  },
  fieldsGrid2: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: "12px"
  },
  bankCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
    gap: "10px"
  },
  bankCard: {
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    padding: "10px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    transition: "all 0.2s ease"
  },
  formFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    background: "linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)",
    borderRadius: "16px",
    marginTop: "8px",
    border: "1px solid #c7d2fe"
  },
  previewStickyContainer: {
    position: "sticky",
    top: "24px"
  },
  previewFixedWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  previewHeading: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: "700",
    fontSize: "0.88rem",
    color: "#10b981",
    background: "rgba(16,185,129,0.08)",
    padding: "8px 14px",
    borderRadius: "10px",
    border: "1px solid rgba(16,185,129,0.2)"
  },
  proformaPaper: {
    background: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 10px 40px -10px rgba(0,0,0,0.12), 0 0 1px 1px rgba(0,0,0,0.05)",
    padding: "20px",
    fontFamily: "Inter, sans-serif",
    color: "#334155",
    border: "1px solid #e2e8f0"
  },
  previewTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "10px"
  },
  previewLogo: {
    maxHeight: "44px",
    maxWidth: "110px",
    objectFit: "contain"
  },
  logoPlaceholder: {
    border: "1px dashed #cbd5e1",
    padding: "8px 12px",
    fontSize: "0.7rem",
    color: "#94a3b8",
    fontWeight: "600",
    borderRadius: "6px"
  },
  taxProformaHeader: {
    fontSize: "1.35rem",
    fontWeight: "900",
    color: "#1e293b",
    letterSpacing: "0.5px",
    textAlign: "center",
    width: "100%"
  },
  metaRow: {
    fontSize: "0.78rem",
    color: "#64748b",
    lineHeight: "1.4"
  },
  subMeta: {
    fontSize: "0.72rem",
    color: "#64748b",
    lineHeight: "1.4"
  },
  previewBoxHeader: {
    fontSize: "0.72rem",
    fontWeight: "800",
    textTransform: "uppercase",
    color: "#4f46e5",
    borderBottom: "1.5px solid #e2e8f0",
    paddingBottom: "3.5px",
    marginBottom: "6px",
    letterSpacing: "0.5px"
  },
  previewBoxBody: {
    fontSize: "0.78rem",
    lineHeight: "1.45",
    color: "#334155"
  },
  previewTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.75rem",
    marginBottom: "12px"
  },
  previewTh: {
    borderBottom: "1.5px solid #cbd5e1",
    padding: "6px 4px",
    fontWeight: "800",
    color: "#1e293b",
    textAlign: "left"
  },

  previewTd: {
    borderBottom: "1px solid #f1f5f9",
    padding: "6px 4px",
    textAlign: "left"
  },

  previewThCenter: {
    borderBottom: "1.5px solid #cbd5e1",
    padding: "6px 4px",
    fontWeight: "800",
    color: "#1e293b",
    textAlign: "center"
  },

  previewTdCenter: {
    borderBottom: "1px solid #f1f5f9",
    padding: "6px 4px",
    textAlign: "center"
  },

  previewThRight: {
    borderBottom: "1.5px solid #cbd5e1",
    padding: "6px 4px",
    fontWeight: "800",
    color: "#1e293b",
    textAlign: "right",
    whiteSpace: "nowrap"
  },

  previewTdRight: {
    borderBottom: "1px solid #f1f5f9",
    padding: "6px 4px",
    textAlign: "right",
    whiteSpace: "nowrap"
  },
  previewTotalsContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px"
  },
  totalRowText: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "0.8rem",
    fontWeight: "500",
    color: "#475569"
  },
  bankDetailPreviewBox: {
    fontSize: "0.72rem",
    background: "#f8fafc",
    padding: "8px",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    marginTop: "8px",
    lineHeight: "1.3"
  },
  previewFooterBox: {
    display: "flex",
    border: "1px solid #000",
    fontSize: "0.72rem",
    marginTop: "auto",
    lineHeight: "1.3"
  },
  listSection: {
    padding: "28px",
    background: "#ffffff",
    borderRadius: "24px"
  },
  listHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #f1f5f9",
    paddingBottom: "16px"
  },
  listTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.9rem",
    textAlign: "left"
  },
  listTh: {
    padding: "12px 16px",
    fontWeight: "700",
    color: "#475569",
    borderBottom: "2px solid #e2e8f0"
  },
  listTr: {
    borderBottom: "1px solid #e2e8f0"
  },
  listTd: {
    padding: "12px 16px"
  }
};